/**
 * Graph normalization
 * -------------------
 * We treat the input as a directed graph (not a strict tree):
 * - Parent/Child relations become directed edges: parent -> child
 * - Spouse relations become undirected "pair" edges (rendered separately, optionally)
 *
 * Cycles are allowed:
 * - We MUST NOT DFS recursively without guards
 * - Layout is computed using SCC condensation later (see layoutGraph.js)
 *
 * Notes:
 * - The API mentions relationToSelf is an enum imported from a .json file.
 *   This module is tolerant: it normalizes relationToSelf via String(...).toUpperCase().
 * - If a connection references a character not present in `characters`,
 *   we create a stub node so edges can still render.
 */

const REL_PARENT = 'PARENT';
const REL_CHILD = 'CHILD';
const REL_SPOUSE = 'SPOUSE';

function normalizeRelation(relationToSelf) {
  if (!relationToSelf) return '';
  return String(relationToSelf).trim().toUpperCase();
}

function makeStubCharacter(id, name) {
  return {
    _id: id,
    name: name || 'Unknown',
    connections: [],
    __isStub: true,
  };
}

/**
 * @param {Array} characters
 * @returns {{
 *   nodesById: Map<string, Object>,
 *   nodeIds: string[],
 *   parentToChildren: Map<string, Set<string>>,
 *   childToParents: Map<string, Set<string>>,
 *   parentChildEdges: Array<{ fromId: string, toId: string }>,
 *   spouseEdges: Array<{ aId: string, bId: string }>
 * }}
 */
function normalizeCharactersGraph(characters) {
  const list = Array.isArray(characters) ? characters : [];

  const nodesById = new Map();
  const parentToChildren = new Map();
  const childToParents = new Map();

  // De-dupe edges (directed for parent/child, undirected for spouse)
  const parentChildEdgeKeys = new Set();
  const spousePairKeys = new Set();

  const ensureNode = (id, nameIfMissing) => {
    if (!id) return null;
    if (!nodesById.has(id)) {
      nodesById.set(id, makeStubCharacter(id, nameIfMissing));
    } else if (nameIfMissing) {
      const existing = nodesById.get(id);
      if (!existing.name || existing.name === 'Unknown') {
        nodesById.set(id, { ...existing, name: nameIfMissing });
      }
    }
    return nodesById.get(id);
  };

  const addParentChildEdge = (fromId, toId) => {
    if (!fromId || !toId) return;
    const key = `${fromId}->${toId}`;
    if (parentChildEdgeKeys.has(key)) return;
    parentChildEdgeKeys.add(key);

    if (!parentToChildren.has(fromId)) parentToChildren.set(fromId, new Set());
    parentToChildren.get(fromId).add(toId);

    if (!childToParents.has(toId)) childToParents.set(toId, new Set());
    childToParents.get(toId).add(fromId);
  };

  const addSpouseEdge = (aId, bId) => {
    if (!aId || !bId) return;
    if (aId === bId) return;
    const [x, y] = aId < bId ? [aId, bId] : [bId, aId];
    const key = `${x}<->${y}`;
    if (spousePairKeys.has(key)) return;
    spousePairKeys.add(key);
  };

  // 1) Register all given characters first (non-stubs)
  list.forEach((c) => {
    if (!c || !c._id) return;
    nodesById.set(c._id, c);
  });

  // 2) Process connections and ensure referenced targets exist
  list.forEach((source) => {
    if (!source || !source._id) return;
    ensureNode(source._id, source.name);

    const connections = Array.isArray(source.connections) ? source.connections : [];
    connections.forEach((conn) => {
      if (!conn) return;
      const targetId = conn.targetCharacterId;
      const targetName = conn.targetCharacterName;

      if (!targetId) return;
      ensureNode(targetId, targetName);

      const rel = normalizeRelation(conn.relationToSelf);
      if (rel === REL_PARENT) {
        // From source's perspective: target is PARENT of source => parent(target) -> child(source)
        addParentChildEdge(targetId, source._id);
      } else if (rel === REL_CHILD) {
        // From source's perspective: target is CHILD of source => parent(source) -> child(target)
        addParentChildEdge(source._id, targetId);
      } else if (rel === REL_SPOUSE) {
        addSpouseEdge(source._id, targetId);
      }
    });
  });

  const nodeIds = Array.from(nodesById.keys());

  const parentChildEdges = Array.from(parentChildEdgeKeys).map((k) => {
    const [fromId, toId] = k.split('->');
    return { fromId, toId };
  });

  const spouseEdges = Array.from(spousePairKeys).map((k) => {
    // k format: "a<->b"
    const [aId, bId] = k.split('<->');
    return { aId, bId };
  });

  return {
    nodesById,
    nodeIds,
    parentToChildren,
    childToParents,
    parentChildEdges,
    spouseEdges,
  };
}

export default normalizeCharactersGraph;
